const FENCE = '```';

function looksLikeProse(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (trimmed.length < 28) return false;
  if (!/[a-z]{3,}/i.test(trimmed)) return false;
  const prosePunctuation = (trimmed.match(/[,.!?]/g) || []).length;
  const symbolDensity = (trimmed.match(/[{}[\];=<>|`$]/g) || []).length;
  return prosePunctuation >= 1 && symbolDensity <= 1;
}

function isCodeLikeLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;

  if (/^#{1,6}\s+/.test(trimmed)) return false;
  if (/^[-*+]\s+/.test(trimmed)) return false;
  if (/^\d+\.\s+/.test(trimmed)) return false;
  if (/^>\s+/.test(trimmed)) return false;
  if (/^\|.*\|$/.test(trimmed)) return false;
  if (looksLikeProse(trimmed)) return false;

  if (/^\t/.test(line) || /^ {4,}\S/.test(line)) return true;
  if (/^\$\s+\S+/.test(trimmed)) return true;
  if (/^(const|let|var|function|class|interface|type|import|export|return|if|else|for|while|switch|case|try|catch|finally)\b/.test(trimmed)) return true;
  if (/^(def|from|print\(|lambda\s+)/.test(trimmed)) return true;
  if (/^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|WITH)\b/i.test(trimmed)) return true;
  if (/^<\/?[a-z][^>]*>$/i.test(trimmed)) return true;
  if (/[{}[\];]|=>|==|!=|<=|>=|\|\||&&/.test(trimmed)) return true;
  if (/^[A-Za-z_][\w.]*\([^)]*\)\s*[:{]?$/.test(trimmed)) return true;
  if (/^@[A-Za-z_]\w*/.test(trimmed)) return true;

  return false;
}

function guessLanguageFromBlock(lines: string[]): string {
  const sample = lines.join('\n');
  if (/\b(import|export)\s+.*from\s+['"]|const\s+\w+\s*=|=>/.test(sample)) return 'typescript';
  if (/\b(def|from\s+\w+\s+import|print\(|if __name__ == ['"]__main__['"])/.test(sample)) return 'python';
  if (/\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bFROM\b/i.test(sample)) return 'sql';
  if (/<\/?[a-z][^>]*>/i.test(sample)) return 'html';
  if (/\b(function|const|let|var)\b/.test(sample)) return 'javascript';
  if (/^\s*\$+\s+/m.test(sample) || /\b(curl|npm|yarn|pnpm|bash)\b/.test(sample)) return 'bash';
  return '';
}

function closeUnbalancedFences(markdown: string): string {
  const fenceCount = (markdown.match(/```/g) || []).length;
  if (fenceCount % 2 === 0) return markdown;
  return `${markdown.trimEnd()}\n\n${FENCE}`;
}

function normalizeInlineMultilineTicks(markdown: string): string {
  return markdown.replace(/`([^`\n]*\n[^`]*)`/g, (_m, inner) => `${FENCE}\n${inner}\n${FENCE}`);
}

function aggressivelyWrapCodeLikeBlocks(markdown: string): string {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let inFence = false;
  let run: string[] = [];

  const flushRun = () => {
    if (run.length === 0) return;
    const meaningful = run.filter((l) => l.trim().length > 0);
    const shouldFence =
      meaningful.length >= 2 ||
      (meaningful.length === 1 && /[{}[\];=<>]|=>/.test(meaningful[0]));

    if (shouldFence) {
      const lang = guessLanguageFromBlock(meaningful);
      out.push(`${FENCE}${lang ? lang : ''}`);
      out.push(...run);
      out.push(FENCE);
    } else {
      out.push(...run);
    }
    run = [];
  };

  for (const line of lines) {
    const fenceLine = line.trim().startsWith(FENCE);
    if (fenceLine) {
      flushRun();
      inFence = !inFence;
      out.push(line);
      continue;
    }

    if (inFence) {
      out.push(line);
      continue;
    }

    if (isCodeLikeLine(line)) {
      run.push(line);
      continue;
    }

    flushRun();
    out.push(line);
  }

  flushRun();
  return out.join('\n');
}

export function aggressivelyFormatMarkdownForCodeBlocks(raw: string): string {
  const input = String(raw || '').replace(/\r\n?/g, '\n');
  if (!input.trim()) return '';

  let output = input;
  output = normalizeInlineMultilineTicks(output);
  output = output.replace(/```([a-z0-9_-]+):\s*$/gim, '```$1');
  output = aggressivelyWrapCodeLikeBlocks(output);
  output = closeUnbalancedFences(output);
  return output;
}
